import { c as _c } from "react/compiler-runtime";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { NumericFormat } from "react-number-format";
import { usePlausible } from "next-plausible";
import { PortableText } from "@portabletext/react";
import { Dispatch } from "@reduxjs/toolkit";
import AnimateHeight from "react-animate-height";
import { Pane, PaneContainer, PaneTitle } from "../Panes.style";
import { ActionBar, AmountInputSection, InfoParagraph, ShareContainer, ShareInputContainer, ShareLink, ShareSelectionSpacer, ShareSelectionWrapper, SharesSelectorContainer, ShowAllOrganizationsLink, SumButtonsWrapper, SumWrapper } from "./SingleCauseAreaPane.style";
import { NextButton } from "../../shared/Buttons/NavigationButtons";
import { ToolTip } from "../../shared/ToolTip/ToolTip";
import { Spinner } from "../../../../Spinner/Spinner";
import { RadioButtonGroup } from "../../../../RadioButton/RadioButtonGroup";
import { EffektButton, EffektButtonVariant } from "../../../../EffektButton/EffektButton";
import { State } from "../../../store/state";
import { RecurringDonation, ShareType } from "../../../types/Enums";
import { setRecurring, setSum, setCauseAreaAmount, setOrgAmount, setCauseAreaDistributionType, setShowAllOrganizations, setHasManuallyEditedPrefilledOrgAmount } from "../../../store/donation/actions";
import { nextPane } from "../../../store/layout/actions";
import { DonationActionTypes } from "../../../store/donation/types";
import { LayoutActionTypes } from "../../../store/layout/types";
import { thousandize } from "../../../../../../../util/formatting";
import { useAmountCalculation } from "../AmountPane/useAmountCalculation";
import { AmountContext, SmartDistributionContext } from "../../../types/WidgetProps";
import { useIsMobile } from "../../../../../../../hooks/useIsMobile";
interface SingleCauseAreaPaneProps {
  nextButtonText: string;
  enableRecurring: boolean;
  enableSingle: boolean;
  singleDonationText: string;
  monthlyDonationText: string;
  amountContext: AmountContext;
  smartDistributionContext: SmartDistributionContext;
}

/**
 * Amount pane used when a platform has a single (active) cause area, e.g. Norway.
 *
 * It reproduces the pre-rewrite ("main") widget experience — recurring toggle,
 * suggested sums, a smart/custom distribution selector with an explanatory
 * description — using main's markup and styling, but is driven by the rewritten
 * donation state. The one thing carried over from the new widget is that custom
 * organization distribution is entered as direct kroner amounts rather than
 * percentages. Operations/tip is intentionally omitted to match the old widget.
 */
export const SingleCauseAreaPane: React.FC<SingleCauseAreaPaneProps> = t0 => {
  const $ = _c(90);
  const {
    nextButtonText,
    enableRecurring,
    enableSingle,
    singleDonationText,
    monthlyDonationText,
    amountContext,
    smartDistributionContext
  } = t0;
  const dispatch = useDispatch();
  const plausible = usePlausible();
  const isMobile = useIsMobile();
  const causeAreas = useSelector(_temp);
  const {
    recurring,
    causeAreaAmounts: t1,
    orgAmounts: t2,
    causeAreaDistributionType: t3,
    prefilledShares: t4,
    showAllOrganizations: t5,
    hasManuallyEditedPrefilledOrgAmount: t6
  } = useSelector(_temp2);
  let t7;
  if ($[0] !== t1) {
    t7 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const causeAreaAmounts = t7;
  let t8;
  if ($[2] !== t2) {
    t8 = t2 === undefined ? {} : t2;
    $[2] = t2;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const orgAmounts = t8;
  let t9;
  if ($[4] !== t3) {
    t9 = t3 === undefined ? {} : t3;
    $[4] = t3;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const causeAreaDistributionType = t9;
  const prefilledShares = t4 === undefined ? null : t4;
  const showAllOrganizations = t5 === undefined ? false : t5;
  const hasManuallyEditedPrefilledOrgAmount = t6 === undefined ? false : t6;
  const activeCauseAreas = causeAreas?.filter(_temp3) ?? [];
  const causeArea = activeCauseAreas[0];
  let t10;
  if ($[6] !== causeAreas) {
    t10 = causeAreas ?? [];
    $[6] = causeAreas;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  const {
    totalAmount
  } = useAmountCalculation("single", causeArea?.id ?? null, t10);
  const currentAmount = causeArea ? causeAreaAmounts[causeArea.id] || 0 : 0;
  const [inputValue, setInputValue] = React.useState(currentAmount);
  let t11;
  let t12;
  if ($[8] !== currentAmount) {
    t11 = () => {
      setInputValue(currentAmount);
    };
    t12 = [currentAmount];
    $[8] = currentAmount;
    $[9] = t11;
    $[10] = t12;
  } else {
    t11 = $[9];
    t12 = $[10];
  }
  React.useEffect(t11, t12);
  const [showErrors, setShowErrors] = React.useState(false);
  let t13;
  if ($[11] !== prefilledShares) {
    t13 = !!prefilledShares && Object.keys(prefilledShares).length > 0;
    $[11] = prefilledShares;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  const hasPrefilledOrgs = t13;
  const isTrackingPrefilledShares = hasPrefilledOrgs && !hasManuallyEditedPrefilledOrgAmount;
  let t14;
  if ($[13] !== currentAmount || $[14] !== isTrackingPrefilledShares || $[15] !== orgAmounts || $[16] !== prefilledShares) {
    t14 = orgId => {
      const share = prefilledShares?.[orgId];
      if (isTrackingPrefilledShares && share !== undefined) {
        return Math.round(share / 100 * currentAmount);
      }
      return orgAmounts[orgId] || 0;
    };
    $[13] = currentAmount;
    $[14] = isTrackingPrefilledShares;
    $[15] = orgAmounts;
    $[16] = prefilledShares;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const displayOrgAmount = t14;
  const effectiveTotalAmount = isTrackingPrefilledShares ? currentAmount : totalAmount;
  if (!causeAreas) {
    let t15;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Pane><PaneContainer><div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%"
          }}><Spinner /></div></PaneContainer></Pane>;
      $[18] = t15;
    } else {
      t15 = $[18];
    }
    return t15;
  }
  if (!causeArea) {
    let t15;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Pane><PaneContainer><div>Missing cause area</div></PaneContainer></Pane>;
      $[19] = t15;
    } else {
      t15 = $[19];
    }
    return t15;
  }
  const activeOrganizations = causeArea.organizations.filter(_temp4).sort(_temp5);
  const hasMultipleOrgs = activeOrganizations.length > 1;
  const distributionType = causeAreaDistributionType[causeArea.id] ?? ShareType.STANDARD;
  const hideAmountInput = hasMultipleOrgs && distributionType === ShareType.CUSTOM && (!hasPrefilledOrgs || hasManuallyEditedPrefilledOrgAmount);
  const suggestedSums = recurring ? amountContext.preset_amounts_recurring : amountContext.preset_amounts_single;
  const setAmount = amount => {
    setInputValue(amount);
    dispatch(setCauseAreaAmount(causeArea.id, amount));
    if (activeOrganizations.length <= 1 && activeOrganizations[0]) {
      dispatch(setOrgAmount(activeOrganizations[0].id, amount));
    }
  };
  const handleDistributionChange = value => {
    dispatch(setCauseAreaDistributionType(causeArea.id, value));
    if (value === ShareType.CUSTOM) {
      const hasSetOrgAmounts = activeOrganizations.some(org_0 => (orgAmounts[org_0.id] || 0) > 0);
      if (!hasSetOrgAmounts) {
        const causeAreaAmount = causeAreaAmounts[causeArea.id] || 0;
        for (const org_1 of causeArea.organizations) {
          if (!org_1.standardShare) {
            continue;
          }
          const orgShare = Math.round(org_1.standardShare / 100 * causeAreaAmount);
          dispatch(setOrgAmount(org_1.id, orgShare));
        }
      }
    }
  };
  const T0 = SumButtonsWrapper;
  const t15 = suggestedSums.map(suggested => <div key={suggested.amount}><EffektButton variant={EffektButtonVariant.SECONDARY} selected={inputValue === suggested.amount} onClick={() => {
      plausible("SelectSuggestedSum", {
        props: {
          sum: suggested.amount
        }
      });
      setAmount(suggested.amount);
    }} noMinWidth={true} data-cy={`suggested-sum-${causeArea.id}-${suggested.amount}`}>{`${suggested.amount ? thousandize(suggested.amount) : "-"} kr`}</EffektButton>{suggested.subtext && <i>{suggested.subtext}</i>}</div>);
  let t16;
  if ($[20] !== T0 || $[21] !== t15) {
    t16 = <T0>{t15}</T0>;
    $[20] = T0;
    $[21] = t15;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== amountContext.custom_amount_text) {
    t17 = <label>{amountContext.custom_amount_text}</label>;
    $[23] = amountContext.custom_amount_text;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const t18 = `sum-${causeArea.id}`;
  const t19 = inputValue > 0 ? inputValue : "";
  const t20 = `donation-sum-input-${causeArea.id}`;
  let t21;
  if ($[25] !== setAmount) {
    t21 = values => setAmount(values.floatValue ?? 0);
    $[25] = setAmount;
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  let t22;
  if ($[27] !== t18 || $[28] !== t19 || $[29] !== t20 || $[30] !== t21) {
    t22 = <span><NumericFormat name={t18} thousandSeparator=" " allowNegative={false} decimalScale={0} type="tel" placeholder="0" value={t19} autoComplete="off" data-cy={t20} onValueChange={t21} /></span>;
    $[27] = t18;
    $[28] = t19;
    $[29] = t20;
    $[30] = t21;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] !== t17 || $[33] !== t22) {
    t23 = <SumWrapper>{t17}{t22}</SumWrapper>;
    $[32] = t17;
    $[33] = t22;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  let t24;
  if ($[35] !== t16 || $[36] !== t23) {
    t24 = <>{t16}{t23}</>;
    $[35] = t16;
    $[36] = t23;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  const amountInput = t24;
  let t25;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <PaneTitle><wbr /></PaneTitle>;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  const t26 = !enableSingle;
  let t27;
  if ($[39] !== singleDonationText || $[40] !== t26) {
    t27 = {
      title: singleDonationText,
      value: RecurringDonation.NON_RECURRING,
      data_cy: "radio-single",
      disabled: t26
    };
    $[39] = singleDonationText;
    $[40] = t26;
    $[41] = t27;
  } else {
    t27 = $[41];
  }
  const t28 = !enableRecurring;
  let t29;
  if ($[42] !== monthlyDonationText || $[43] !== t28) {
    t29 = {
      title: monthlyDonationText,
      value: RecurringDonation.RECURRING,
      data_cy: "radio-recurring",
      disabled: t28
    };
    $[42] = monthlyDonationText;
    $[43] = t28;
    $[44] = t29;
  } else {
    t29 = $[44];
  }
  let t30;
  if ($[45] !== t27 || $[46] !== t29) {
    t30 = [t27, t29];
    $[45] = t27;
    $[46] = t29;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  let t31;
  if ($[48] !== dispatch) {
    t31 = value_0 => dispatch(setRecurring(value_0 as RecurringDonation));
    $[48] = dispatch;
    $[49] = t31;
  } else {
    t31 = $[49];
  }
  let t32;
  if ($[50] !== recurring || $[51] !== t30 || $[52] !== t31) {
    t32 = <RadioButtonGroup options={t30} selected={recurring} onSelect={t31} />;
    $[50] = recurring;
    $[51] = t30;
    $[52] = t31;
    $[53] = t32;
  } else {
    t32 = $[53];
  }
  const t33 = hideAmountInput ? 0 : "auto";
  const t34 = !hasMultipleOrgs;
  let t35;
  if ($[54] !== amountInput || $[55] !== t34) {
    t35 = <AmountInputSection $padBottom={t34}>{amountInput}</AmountInputSection>;
    $[54] = amountInput;
    $[55] = t34;
    $[56] = t35;
  } else {
    t35 = $[56];
  }
  let t36;
  if ($[57] !== t33 || $[58] !== t35) {
    t36 = <AnimateHeight height={t33} animateOpacity={true} duration={300}>{t35}</AnimateHeight>;
    $[57] = t33;
    $[58] = t35;
    $[59] = t36;
  } else {
    t36 = $[59];
  }
  let t37;
  if ($[60] !== activeOrganizations || $[61] !== dispatch || $[62] !== displayOrgAmount || $[63] !== distributionType || $[64] !== handleDistributionChange || $[65] !== hasMultipleOrgs || $[66] !== hasPrefilledOrgs || $[67] !== isMobile || $[68] !== prefilledShares || $[69] !== showAllOrganizations || $[70] !== smartDistributionContext) {
    t37 = hasMultipleOrgs && <ShareSelectionSpacer><RadioButtonGroup options={[{
        title: smartDistributionContext.smart_distribution_radiobutton_text,
        value: ShareType.STANDARD,
        data_cy: "radio-smart-share"
      }, {
        title: smartDistributionContext.custom_distribution_radiobutton_text,
        value: ShareType.CUSTOM,
        data_cy: "radio-custom-share"
      }]} selected={distributionType} onSelect={value_1 => handleDistributionChange(value_1 as ShareType)} /><AnimateHeight height={distributionType === ShareType.STANDARD && smartDistributionContext.smart_distribution_description ? "auto" : 0} animateOpacity={true} duration={300}><InfoParagraph>{smartDistributionContext.smart_distribution_description && <PortableText value={smartDistributionContext.smart_distribution_description} />}</InfoParagraph></AnimateHeight><AnimateHeight height={distributionType === ShareType.CUSTOM ? "auto" : 0} animateOpacity={true} duration={300}><SharesSelectorContainer><ShareSelectionWrapper><ShareContainer>{(hasPrefilledOrgs && !showAllOrganizations ? activeOrganizations.filter(org_2 => prefilledShares?.[org_2.id] !== undefined) : activeOrganizations).map(org_3 => <ShareInputContainer key={org_3.id}><div><ShareLink href={org_3.informationUrl} target={isMobile ? "_blank" : undefined} rel={isMobile ? "noopener noreferrer" : undefined}>{org_3.widgetDisplayName || org_3.name}</ShareLink>{org_3.widgetContext && <ToolTip text={org_3.widgetContext} />}</div><NumericFormat id={`org-${org_3.id}`} aria-label={org_3.widgetDisplayName || org_3.name} type="tel" placeholder="0" value={displayOrgAmount(org_3.id) || ""} step={1} decimalScale={0} allowNegative={false} thousandSeparator=" " autoComplete="off" data-cy={`org-${org_3.id}`} onValueChange={(values_0, sourceInfo) => {
                  if (sourceInfo.source !== "event") {
                    return;
                  }
                  if (prefilledShares?.[org_3.id] !== undefined) {
                    dispatch(setHasManuallyEditedPrefilledOrgAmount(true));
                  }
                  dispatch(setOrgAmount(org_3.id, values_0.floatValue ?? 0));
                }} /></ShareInputContainer>)}</ShareContainer>{hasPrefilledOrgs && !showAllOrganizations && <ShowAllOrganizationsLink type="button" onClick={() => dispatch(setShowAllOrganizations(true))} data-cy="show-all-organizations-button">{smartDistributionContext.show_all_organizations_text} ↓</ShowAllOrganizationsLink>}</ShareSelectionWrapper></SharesSelectorContainer></AnimateHeight></ShareSelectionSpacer>;
    $[60] = activeOrganizations;
    $[61] = dispatch;
    $[62] = displayOrgAmount;
    $[63] = distributionType;
    $[64] = handleDistributionChange;
    $[65] = hasMultipleOrgs;
    $[66] = hasPrefilledOrgs;
    $[67] = isMobile;
    $[68] = prefilledShares;
    $[69] = showAllOrganizations;
    $[70] = smartDistributionContext;
    $[71] = t37;
  } else {
    t37 = $[71];
  }
  let t38;
  if ($[72] !== t32 || $[73] !== t36 || $[74] !== t37) {
    t38 = <div>{t25}{t32}{t36}{t37}</div>;
    $[72] = t32;
    $[73] = t36;
    $[74] = t37;
    $[75] = t38;
  } else {
    t38 = $[75];
  }
  const t39 = showErrors && effectiveTotalAmount <= 0;
  let t40;
  if ($[76] !== currentAmount || $[77] !== dispatch || $[78] !== effectiveTotalAmount || $[79] !== isTrackingPrefilledShares || $[80] !== prefilledShares || $[81] !== setShowErrors) {
    t40 = () => {
      if (effectiveTotalAmount <= 0) {
        setShowErrors(true);
        return;
      }
      if (isTrackingPrefilledShares && prefilledShares) {
        Object.entries(prefilledShares).forEach(t41 => {
          const [orgId_0, share_0] = t41;
          dispatch(setOrgAmount(Number(orgId_0), Math.round(share_0 / 100 * currentAmount)));
        });
      }
      dispatch(setSum(effectiveTotalAmount));
      dispatch(nextPane());
    };
    $[76] = currentAmount;
    $[77] = dispatch;
    $[78] = effectiveTotalAmount;
    $[79] = isTrackingPrefilledShares;
    $[80] = prefilledShares;
    $[81] = setShowErrors;
    $[82] = t40;
  } else {
    t40 = $[82];
  }
  let t41;
  if ($[83] !== nextButtonText || $[84] !== t39 || $[85] !== t40) {
    t41 = <ActionBar><NextButton disabled={t39} onClick={t40} data-cy="next-button">{nextButtonText}</NextButton></ActionBar>;
    $[83] = nextButtonText;
    $[84] = t39;
    $[85] = t40;
    $[86] = t41;
  } else {
    t41 = $[86];
  }
  let t42;
  if ($[87] !== t38 || $[88] !== t41) {
    t42 = <Pane><PaneContainer>{t38}{t41}</PaneContainer></Pane>;
    $[87] = t38;
    $[88] = t41;
    $[89] = t42;
  } else {
    t42 = $[89];
  }
  return t42;
};
function _temp(state) {
  return state.layout.causeAreas;
}
function _temp2(state_0) {
  return state_0.donation;
}
function _temp3(ca) {
  return ca.isActive;
}
function _temp4(org) {
  return org.isActive;
}
function _temp5(a, b) {
  return a.ordering - b.ordering;
}